import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { emailScannerImageUrl, getLogoUrlForIntegrationProvider } from '../../../../../models/UiUtils';
import { useGetExternalIntegrationsQuery, useGetGoogleBusinessMetadata } from '../../../../../queries';
import {
	AccountOperationsViewModel,
	AdminPermission,
	AdminUserSessionContext,
} from '../../../../../viewmodels/AdminViewModels';
import ams360Image from '../../../../../web/assets/ams360.png';
import googleBusinessProfileLogo from '../../../../../web/assets/google_business_profile_logo.png';
import hawkSoftLogo from '../../../../../web/assets/hawksoft_logo.png';
import hubspotLogo from '../../../../../web/assets/hubspot_logo.jpg';
import clioLogo from '../../../../../web/assets/logo_clio.png';
import donorPerfectLogo from '../../../../../web/assets/logo_donorperfect.png';
import epicLogo from '../../../../../web/assets/logo_epic.svg';
import ezlynxLogo from '../../../../../web/assets/logo_ezlynx.png';
import nasasoftLogo from '../../../../../web/assets/logo_nasasoft.png';
import qqLogo from '../../../../../web/assets/logo_qq_catalyst.png';
import redtailLogo from '../../../../../web/assets/logo_redtail.png';
import wealthboxLogo from '../../../../../web/assets/logo_wealthbox.png';
import xanatekLogo from '../../../../../web/assets/logo_xanatek.jpg';
import xeroLogo from '../../../../../web/assets/logo_xero_blue.png';
import qboLogo from '../../../../../web/assets/qb-logo-stacked.svg';
import { Button } from '../../../../../web/components-v2/Button';
import { BlueCardWithCta } from '../../../../../web/components/cards/BlueCardWithCta';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { CustomLeadParserModal } from '../../../../../web/components/CustomLeadParserModal';
import { IntegrationCard } from '../../../../../web/components/integrations/IntegrationCard';
import { LeadParserCard } from '../../../../../web/components/LeadParserCard';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { TestLeadParserModal } from '../../../../../web/components/TestLeadParserModal';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { GoogleBusinessSetupModal } from '../../../../components/integrations/GoogleBusinessSetupModal';
import { HawksoftSetupModal } from '../../../../components/integrations/hawkSoft/HawksoftSetup';
import { AdminAccountIntegrationsViewModel } from '../../../../viewModels';
import { styleSheet } from './styles';

const accountCleanUpModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Delete',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const needAccountResetModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCancel: true,
		representedObject: false,
		title: 'OK',
	},
];

const confirmOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCancel: true,
		representedObject: true,
		title: 'Cancel',
	},
	{
		isCancel: false,
		representedObject: false,
		title: 'Enable DonorPerfect',
	},
];

interface IProps {
	accountVM?: AccountOperationsViewModel;
	adminUserSession?: AdminUserSessionContext;
	className?: string;
	disableHawksoft?(event: React.MouseEvent<HTMLElement>): void;
	enableHawksoft?(event: React.MouseEvent<HTMLElement>): void;
	onHawksoftSetupModalRequestClose?(agencyId?: string, cancel?: boolean): void;
	showHawksoftSetup?: boolean;
	styles?: StyleDeclarationValue[];
}

interface IEnableEmailScanConfig {
	scannerId: string;
	shouldEnableScanner: boolean;
	type: Api.EmailScannerType;
}

const _AdminIntegrations = ({
	accountVM,
	adminUserSession,
	className,
	onHawksoftSetupModalRequestClose,
	showHawksoftSetup,
	styles,
}: IProps) => {
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const history = useHistory();
	const { logApiError } = useEventLogging();
	const [manageParserModalIsVisible, setManageParserModalIsVisible] = useState(false);
	const [testParserModalIsVisible, setTestParserModalIsVisible] = useState(false);
	const [parserToManage, setParserToManage] = useState(null);
	const [parserToTest, setParserToTest] = useState(null);
	const [processing, setProcessing] = useState(false);
	const [showGoogleBusinessSetup, setShowGoogleBusinessSetup] = useState(false);

	const [account] = useState<AccountOperationsViewModel>(
		new AccountOperationsViewModel(userSession as AdminUserSessionContext, accountVM.account)
	);
	const [emailScannersVm] = useState<Api.EmailScannersViewModel>(() => {
		const vm = new Api.EmailScannersViewModel(userSession as AdminUserSessionContext, accountVM.account);
		vm.impersonate({ account: accountVM.account });
		return vm;
	});
	const [accountIntegrationsVm] = useState<AdminAccountIntegrationsViewModel>(
		new AdminAccountIntegrationsViewModel(userSession as AdminUserSessionContext, accountVM.account)
	);

	const impersonationContext = useMemo(() => {
		return accountIntegrationsVm?.account
			? {
					account: { id: accountIntegrationsVm.account.id },
				}
			: null;
	}, [accountIntegrationsVm]);

	const { data: googleBusinessMetadata } = useGetGoogleBusinessMetadata({
		impersonationContext,
	});

	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({
		impersonationContext,
	});

	const isGoogleBusinessEnabled =
		googleBusinessMetadata?.connectionState === Api.GoogleBusinessConnectionState.Connected;

	// Check if V2 integrations are enabled AND configured
	const salesforceV2 = externalIntegrations?.find(i => i.provider === Api.IntegrationProvider.Salesforce);
	const isSalesforceV2EnabledAndConfigured = salesforceV2?.enabled && !salesforceV2?.configuration?.needsConfiguration;

	const qboV2 = externalIntegrations?.find(i => i.provider === Api.IntegrationProvider.QuickBooksOnline);
	const isQBOV2EnabledAndConfigured = qboV2?.enabled && !qboV2?.configuration?.needsConfiguration;

	useEffect(() => {
		emailScannersVm.loadStats();
		account.load().catch((error: Api.IOperationResultNoValue) => {
			logApiError('GetAccount-Error', error);
			errorMessages.push({
				messages: ['Could not load the account'],
			});
		});

		accountIntegrationsVm.load().catch((error: Api.IOperationResultNoValue) => {
			if (error.systemCode !== 404) {
				logApiError('GetIntegrations-Error', error);
				errorMessages.push({});
			}
		});
	}, [account, accountIntegrationsVm, emailScannersVm, errorMessages, logApiError]);

	const enableScanner = async ({ scannerId, shouldEnableScanner, type }: IEnableEmailScanConfig) => {
		const enableText = shouldEnableScanner ? 'enable' : 'disable';
		setProcessing(true);

		try {
			const action =
				type === Api.EmailScannerType.EmailScanner
					? emailScannersVm.enableEmailScanner(scannerId)
					: emailScannersVm.enableCustomLeadParser(scannerId);
			await action;

			toast.success(
				`${
					type === Api.EmailScannerType.EmailScanner ? 'Email scanner' : 'Custom lead source'
				} ${enableText}d successfully`
			);
			setProcessing(false);
		} catch (error) {
			logApiError('EnableEmailScannerParser-Error', error);
			errorMessages.pushApiError(
				error,
				`Failed to ${enableText} ${type === Api.EmailScannerType.EmailScanner ? 'email scanner' : 'custom lead source'}`
			);
			setProcessing(false);
		}
	};

	const onAddNewCustomLeadParserClick = () => {
		setManageParserModalIsVisible(true);
		setParserToManage(null);
	};

	const onEnableClick = (id: string, enable: boolean, type: Api.EmailScannerType) => {
		enableScanner({
			shouldEnableScanner: enable,
			scannerId: id,
			type,
		});
	};

	const onLeadParserCheckboxChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const automationFeatures: Api.IAutomationFeatures = {
			...account?.account?.features?.automation,
			autoStartForLeads: e.target.checked,
		};
		await account?.updateAutomationFeatures(automationFeatures)?.catch((error: Api.IOperationResultNoValue) => {
			logApiError('UpdateAccountFeatures-Error', error);
			errorMessages.push({
				messages: ['Could not update the account features'],
			});
		});
		await account.load().catch((error: Api.IOperationResultNoValue) => {
			logApiError('GetAccount-Error', error);
			errorMessages.push({
				messages: ['Could not load the account'],
			});
		});
	};

	const onManageLeadParser = async (parser?: Api.ICustomLeadParser, cancel?: boolean) => {
		setManageParserModalIsVisible(parser && !cancel);
		setParserToManage(parser && !cancel ? parser : null);
	};

	const onTestLeadParser = async (parser?: Api.ICustomLeadParser) => {
		setTestParserModalIsVisible(!!parser);
		setParserToTest(parser || null);
	};

	const onManageParserDelete = async (parser: Api.ICustomLeadParser) => {
		setProcessing(true);

		try {
			await emailScannersVm?.deleteCustomLeadParser(parser.id);
			toast.success(`Custom lead source deleted successfully`);
			setManageParserModalIsVisible(false);
			setProcessing(false);
		} catch (error) {
			setProcessing(false);
			logApiError('DeleteCustomLeadParser-Error', error);
			errorMessages.pushApiError(error, 'Error deleting custom lead parser');
		}
	};

	const onManageParserSave = async (parser: Api.ICustomLeadParser, isNew: boolean) => {
		setProcessing(true);
		try {
			const parserAction = isNew
				? emailScannersVm?.createCustomLeadParser(parser)
				: emailScannersVm?.updateCustomLeadParser(parser);
			await parserAction;
			toast.success(`Custom lead source ${isNew ? 'created' : 'updated'} successfully`);
			setManageParserModalIsVisible(false);
			setProcessing(false);
		} catch (error) {
			setProcessing(false);
			logApiError('SaveCustomLeadParser-Error', error);
			errorMessages.pushApiError(error, 'Error creating custom lead source');
		}
	};

	const onRenderManageButtons = (integrationType: Api.ConfigurableIntegrationType, isEnabled: boolean) => {
		return isEnabled ? (
			<div className={css(styleSheet.buttonContainer)}>
				<Button variant='secondary' size='sm' onClick={onManage(integrationType)}>
					Manage
				</Button>
				<Button variant='destructive' size='sm' onClick={onToggleEnabled(integrationType, false)}>
					Disable
				</Button>
			</div>
		) : (
			<Button variant='secondary' size='sm' onClick={onToggleEnabled(integrationType, true)}>
				Enable
			</Button>
		);
	};

	const onManage = (integrationType: Api.ConfigurableIntegrationType) => () => {
		history.push(`/accounts/${accountIntegrationsVm.account.id}/integration/${integrationType}`);
	};

	const [isAccountCleanUpModalOpen, setIsAccountCleanUpModalOpen] = React.useState(false);
	const [isConfirmingDonorPerfectOpen, setIsConfirmingDonorPerfectOpen] = React.useState(false);
	const [isNeedResetModalOpen, setIsNeedResetModalOpen] = React.useState(false);
	const [accountCleanUpIntegrationType, setAccountCleanUpIntegrationType] =
		React.useState<Api.ConfigurableIntegrationType>(null);
	const [accountCleanUpIntegrationEnabled, setAccountCleanUpIntegrationEnabled] = React.useState(false);

	const onToggleEnabled = (integrationType: Api.ConfigurableIntegrationType, enabled: boolean) => async () => {
		setAccountCleanUpIntegrationType(integrationType);
		setAccountCleanUpIntegrationEnabled(enabled);

		if (enabled === true) {
			const enableAbility = await accountIntegrationsVm.getIntegrationEnableAbility(integrationType);
			if (enableAbility === Api.EnableAbility.CleanUpRequired) {
				const hasResetAccountPermission = !!adminUserSession.hasPermission(AdminPermission.ResetAccount);
				if (hasResetAccountPermission) {
					setIsAccountCleanUpModalOpen(true);
				} else {
					setIsNeedResetModalOpen(true);
				}
			} else if (enableAbility === Api.EnableAbility.CannotEnable) {
				toast.error(`There is already an enabled integration on this account.`);
			} else {
				enableIntegration(integrationType, enabled);
			}
		} else {
			enableIntegration(integrationType, enabled);
		}
	};

	const onAccountCleanUpModalConfirmationDialogRequestClose = (
		result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (!!result?.isDestructive && !canceled) {
			account
				.resetAccountAndIntegration(accountIntegrationsVm?.account)
				?.then(() => {
					enableIntegration(accountCleanUpIntegrationType, accountCleanUpIntegrationEnabled);
				})
				?.catch((error: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(error);
					logApiError('ResetAccountAndIntegrationError from integration switch', error);
					return;
				});
		}

		setIsAccountCleanUpModalOpen(false);
	};

	const onDonorPerfectConfirmRequestClose = async (
		_result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (!canceled) {
			await onToggleEnabled(Api.ConfigurableIntegrationType.DonorPerfect, true)();
		}

		setIsConfirmingDonorPerfectOpen(false);
	};

	const enableIntegration = (integrationType: Api.ConfigurableIntegrationType, enabled: boolean) => {
		switch (integrationType) {
			case Api.ConfigurableIntegrationType.Ams360:
				accountIntegrationsVm.ams360.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.HawkSoft:
				accountIntegrationsVm.hawksoft.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Redtail:
				accountIntegrationsVm.redtail.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Eclipse:
				accountIntegrationsVm.eclipse.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Clio:
				accountIntegrationsVm.clio.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.EzLynx:
				accountIntegrationsVm.ezlynx.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Xanatek:
				accountIntegrationsVm.xanatek.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Epic:
				accountIntegrationsVm.epic.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.QQ:
				accountIntegrationsVm.qq.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.HubSpot:
				accountIntegrationsVm.hubspot.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Salesforce:
				accountIntegrationsVm.salesforce.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Wealthbox:
				accountIntegrationsVm.wealthbox.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.QuickBooksOnline:
				accountIntegrationsVm.qbo.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Xero:
				accountIntegrationsVm.xero.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.DonorPerfect:
				accountIntegrationsVm.donorPerfect.configure({ enabled });
				break;
			default:
				break;
		}
	};

	const isAms360Enabled = accountIntegrationsVm?.ams360?.enabled;
	const isHawksoftEnabled = accountIntegrationsVm?.hawksoft?.enabled;
	const isHubspotEnabled = accountIntegrationsVm?.hubspot?.enabled;
	const isRedtailEnabled = accountIntegrationsVm?.redtail?.enabled;
	const isEclipseEnabled = accountIntegrationsVm?.eclipse?.enabled;
	const isClioEnabled = accountIntegrationsVm?.clio?.enabled;
	const isEzLynxEnabled = accountIntegrationsVm?.ezlynx?.enabled;
	const isXanatekEnabled = accountIntegrationsVm?.xanatek?.enabled;
	const isEpicEnabled = accountIntegrationsVm?.epic?.enabled;
	const isQQEnabled = accountIntegrationsVm?.qq?.enabled;
	const isSalesforceEnabled = accountIntegrationsVm?.salesforce?.enabled;
	const isWealthboxEnabled = accountIntegrationsVm?.wealthbox?.enabled;
	const isQBOEnabled = accountIntegrationsVm?.qbo?.enabled;
	const isXeroEnabled = accountIntegrationsVm?.xero?.enabled;
	const isDonorPerfectEnabled = accountIntegrationsVm?.donorPerfect?.enabled;
	const accountsAccount = account?.account;
	if (!accountsAccount) {
		return null;
	}

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<div className={css(styleSheet.checkboxContainer)}>
				<Checkbox
					checked={accountsAccount?.features?.automation?.autoStartForLeads ?? false}
					disabled={accountVM?.isBusy}
					id='admin-integrations-lead-parser'
					name='admin-integrations-lead-parser'
					onChange={onLeadParserCheckboxChanged}
				>
					<span>Automatically approve Automations triggered by lead parsers</span>
				</Checkbox>
			</div>

			<div className={css(styleSheet.items)}>
				<ConditionalAccess permission={AdminPermission.ModifyAccount}>
					<BlueCardWithCta
						ctaText='Add custom lead source'
						onClick={() => onAddNewCustomLeadParserClick()}
						styles={[styleSheet.item]}
						text='Are you getting leads from another source?'
					/>
				</ConditionalAccess>
				{emailScannersVm?.customLeadParsers?.length > 0 &&
					emailScannersVm.customLeadParsers.map(parser => {
						return (
							<LeadParserCard
								enabled={!parser.isDisabled}
								logo={<div className={css(styleSheet.textLogo)}>{parser.name ?? parser.tag}</div>}
								id={parser.id}
								key={parser.id || parser?.name}
								leadsProcessed={parser.stats?.successCount}
								onEnableClick={adminUserSession.hasPermission(AdminPermission.ModifyAccount) && onEnableClick}
								onManageClick={() => {
									if (parser.isDisabled || !adminUserSession.hasPermission(AdminPermission.ModifyAccount)) {
										return;
									}
									onManageLeadParser(parser);
								}}
								onTestClick={() => {
									if (parser.isDisabled || !adminUserSession.hasPermission(AdminPermission.ModifyAccount)) {
										return;
									}
									onTestLeadParser(parser);
								}}
								tags={[parser.tag]}
								type={Api.EmailScannerType.CustomLeadParser}
								styles={[styleSheet.item]}
							/>
						);
					})}
				{(accountsAccount.additionalInfo.industry === Api.Industry.Insurance || isHawksoftEnabled) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isHawksoftEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={hawkSoftLogo} />}
						key='hawksoft'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.HawkSoft, isHawksoftEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{accountsAccount.features.aida?.enabled && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isHubspotEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={hubspotLogo} />}
						key='hubspot'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.HubSpot, isHubspotEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{(accountsAccount.additionalInfo.industry === Api.Industry.Insurance || isAms360Enabled) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isAms360Enabled}
						icon={<img className={css(styleSheet.itemIcon)} src={ams360Image} />}
						key='ams360'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Ams360, isAms360Enabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{(accountsAccount.additionalInfo.industry === Api.Industry.Insurance || isEclipseEnabled) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isEclipseEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={nasasoftLogo} />}
						key='eclipse'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Eclipse, isEclipseEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{(accountsAccount.additionalInfo.industry === Api.Industry.Insurance || isEzLynxEnabled) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isEzLynxEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={ezlynxLogo} />}
						key='ezlynx'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.EzLynx, isEzLynxEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{(accountsAccount.additionalInfo.industry === Api.Industry.Insurance || isXanatekEnabled) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isXanatekEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={xanatekLogo} />}
						key={Api.ConfigurableIntegrationType.Xanatek}
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Xanatek, isXanatekEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{(accountsAccount.additionalInfo.industry === Api.Industry.Insurance || isEpicEnabled) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isEpicEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={epicLogo} />}
						key='epic'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Epic, isEpicEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{(accountsAccount.additionalInfo.industry === Api.Industry.Insurance || isQQEnabled) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isQQEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={qqLogo} />}
						key='qq'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.QQ, isQQEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{/* V1 QBO: Only show if V1 is enabled AND V2 is not (enabled AND configured), or if dev */}
				{(adminUserSession.hasPermission(AdminPermission.OnlyForDevelopers) ||
					(isQBOEnabled && !isQBOV2EnabledAndConfigured)) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isQBOEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={qboLogo} alt='QuickBooks Online Logo' />}
						key='quickbooksonline'
						onRenderCtaButton={() =>
							onRenderManageButtons(Api.ConfigurableIntegrationType.QuickBooksOnline, isQBOEnabled)
						}
						styles={[styleSheet.item]}
					/>
				)}
				<ConditionalAccess permission={AdminPermission.OnlyForDevelopers}>
					{(accountsAccount.additionalInfo.industry === Api.Industry.Accounting || isXeroEnabled) && (
						<IntegrationCard
							canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
							enabled={isXeroEnabled}
							icon={<img className={css(styleSheet.itemIcon)} src={xeroLogo} />}
							key='xero'
							onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Xero, isXeroEnabled)}
							styles={[styleSheet.item]}
						/>
					)}
				</ConditionalAccess>
				{(accountsAccount.additionalInfo.industry === Api.Industry.Financial || isRedtailEnabled) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isRedtailEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={redtailLogo} />}
						key='redtail'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Redtail, isRedtailEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{(accountsAccount.additionalInfo.industry === Api.Industry.Financial || isWealthboxEnabled) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isWealthboxEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={wealthboxLogo} />}
						key='wealthbox'
						onRenderCtaButton={() =>
							onRenderManageButtons(Api.ConfigurableIntegrationType.Wealthbox, isWealthboxEnabled)
						}
						styles={[styleSheet.item]}
					/>
				)}
				{accountsAccount.additionalInfo.industry === Api.Industry.Legal || isClioEnabled ? (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isClioEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={clioLogo} />}
						key='clio'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Clio, isClioEnabled)}
						styles={[styleSheet.item]}
					/>
				) : null}
				{accountsAccount.additionalInfo.industry === Api.Industry.NonProfit || isDonorPerfectEnabled ? (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isDonorPerfectEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={donorPerfectLogo} />}
						key='donorPerfect'
						onRenderCtaButton={() => {
							if (isDonorPerfectEnabled) {
								return onRenderManageButtons(Api.ConfigurableIntegrationType.DonorPerfect, isDonorPerfectEnabled);
							}
							return (
								<Button variant='secondary' size='sm' onClick={() => setIsConfirmingDonorPerfectOpen(true)}>
									Manage
								</Button>
							);
						}}
						styles={[styleSheet.item]}
					/>
				) : null}
				{/* V1 Salesforce: Only show if V1 is enabled AND V2 is not (enabled AND configured), or if dev */}
				{(adminUserSession.hasPermission(AdminPermission.OnlyForDevelopers) ||
					(isSalesforceEnabled && !isSalesforceV2EnabledAndConfigured)) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isSalesforceEnabled}
						icon={
							<img
								className={css(styleSheet.itemIconLarge)}
								src={getLogoUrlForIntegrationProvider(Api.ConfigurableIntegrationType.Salesforce)}
							/>
						}
						key='salesforce'
						onRenderCtaButton={() =>
							onRenderManageButtons(Api.ConfigurableIntegrationType.Salesforce, isSalesforceEnabled)
						}
						styles={[styleSheet.item]}
					/>
				)}
				<IntegrationCard
					canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
					enabled={isGoogleBusinessEnabled}
					icon={<img className={css(styleSheet.itemIcon)} src={googleBusinessProfileLogo} />}
					key='google-business-integration'
					onRenderCtaButton={() => {
						if (!isGoogleBusinessEnabled) {
							return null;
						}
						return (
							<Button variant='secondary' size='sm' onClick={() => setShowGoogleBusinessSetup(true)}>
								Manage
							</Button>
						);
					}}
					styles={[styleSheet.item, styleSheet.googleBusinessItem]}
				/>
				{emailScannersVm?.emailScanners?.length > 0 &&
					emailScannersVm?.emailScanners.map(scanner => {
						return (
							<LeadParserCard
								enabled={!scanner.isDisabled}
								logo={<img src={emailScannerImageUrl(scanner.id)} />}
								id={scanner.id}
								key={scanner.id || scanner?.name}
								leadsProcessed={scanner.stats?.successCount}
								onEnableClick={adminUserSession.hasPermission(AdminPermission.ModifyAccount) && onEnableClick}
								tags={[scanner.tag]}
								type={Api.EmailScannerType.EmailScanner}
								styles={[styleSheet.item]}
							/>
						);
					})}
			</div>
			<HawksoftSetupModal
				modalProps={{
					isOpen: showHawksoftSetup,
					onRequestClose: onHawksoftSetupModalRequestClose,
				}}
			/>
			<CustomLeadParserModal
				isProcessing={processing}
				modalProps={{
					isOpen: manageParserModalIsVisible,
					onRequestClose: () => onManageLeadParser(),
				}}
				onCancel={onManageLeadParser}
				onDelete={adminUserSession.hasPermission(AdminPermission.ModifyAccount) && onManageParserDelete}
				onSave={adminUserSession.hasPermission(AdminPermission.ModifyAccount) && onManageParserSave}
				parser={parserToManage}
				tagVM={accountVM}
			/>
			<TestLeadParserModal
				modalProps={{
					isOpen: testParserModalIsVisible,
					onRequestClose: () => onTestLeadParser(),
				}}
				onClose={() => onTestLeadParser()}
				parser={parserToTest}
				tagVM={accountVM}
			/>
			<GoogleBusinessSetupModal
				impersonationContext={impersonationContext}
				isOpen={showGoogleBusinessSetup}
				setIsOpen={setShowGoogleBusinessSetup}
				onRequestClose={() => setShowGoogleBusinessSetup(false)}
			/>
			{processing && (
				<div className={css(baseStyleSheet.absoluteCenter)}>
					<LoadingSpinner type='large' />
				</div>
			)}
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isAccountCleanUpModalOpen,
					onRequestClose: onAccountCleanUpModalConfirmationDialogRequestClose,
				}}
				options={accountCleanUpModalOptions}
				title='There were integrations previously enabled on this account.  To permanently delete all previous data including contacts, companies, tags, households, connection types, text messages, donations, and integration settings, click Delete.  Otherwise, click Cancel and contact Engineering for help.'
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isNeedResetModalOpen,
					onRequestClose: () => setIsNeedResetModalOpen(false),
				}}
				options={needAccountResetModalOptions}
				title='There were integrations previously enabled on this account. Please contact Support or Engineering to request cleanup.'
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isConfirmingDonorPerfectOpen,
					onRequestClose: onDonorPerfectConfirmRequestClose,
				}}
				options={confirmOptions}
				title='Enabling DonorPerfect will result in all existing donations being archived and users will no longer be able to manually import or edit donation data in Levitate.'
			/>
		</div>
	);
};

export const AdminIntegrations = observer(_AdminIntegrations);
